import { exerciseName } from "../model/exerciseNames";
import {
  EXERCISE_GROUP_DESCRIPTIONS,
  EXERCISE_GROUP_TYPE,
  WORKOUT_TYPE,
  equipmentLabel,
  exerciseGroupTypeLabel,
} from "../model/workoutEnums";
import {
  ExerciseGroupV2Model,
  ExerciseV1Model,
  ExerciseV2Model,
  SetV2Model,
} from "../model/workoutPlanTypes";

/**
 * Pure selectors over V2 exercise groups, mirroring the mobile
 * `WorkoutDetailsV2Screen` / `WorkoutV2SingleExerciseElement` rules without
 * their logging state.
 */

export const EMPTY_SET: SetV2Model = {
  reps: 0,
  weight_kg: 0,
  work_time_minutes: 0,
  work_zone: 0,
  recovery_time_seconds: 0,
  rpm: 0,
  speed: 0,
  watts: 0,
  stages: 0,
};

/** The exercise as it should be drawn: the selected alternative when one is in effect. */
export type ResolvedExercise = {
  id: string;
  nameId: number;
  title: string;
  type: number;
  subType: number;
  equipment: number;
  muscleGroup: number;
  recoverySeconds: number;
  /** Never empty: an exercise without sets gets one all-zero set (mobile parity). */
  sets: SetV2Model[];
  photoUrl?: string;
  videoUrl?: string;
  isSwapped: boolean;
  /** A recovery period between exercises, not a real exercise. */
  isRecovery: boolean;
};

const MOBILE_PHOTO_BASE = "https://mypnoe.blob.core.windows.net/workout/";

/**
 * The mobile app's fallback photo URL: words capitalised and joined with
 * underscores, `.png` ("Assisted Bulgarian Split Squat" →
 * …/Assisted_Bulgarian_Split_Squat.png). Empty for an empty name.
 */
export const exercisePhotoUrlFromName = (name: string): string => {
  const words = name
    .split(/[\s\-(),./]+/)
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1));
  return words.length ? `${MOBILE_PHOTO_BASE}${words.join("_")}.png` : "";
};

export const exerciseInEffect = (exercise: ExerciseV2Model): ResolvedExercise => {
  const selected = exercise.alternatives?.find((alt) => alt.is_selected);
  const nameId = selected?.name ?? exercise.name;
  const rawTitle =
    selected?.title ?? selected?.fields?.exercise_name ?? exercise.title;
  const title = exerciseName(nameId, rawTitle);
  const sets = selected?.sets?.length ? selected.sets : exercise.sets;
  const fields = selected?.fields ?? exercise.fields;
  const isRecovery =
    exercise.type === WORKOUT_TYPE.RestDay || (exercise.name === 0 && !selected);

  return {
    id: exercise.id,
    nameId,
    title,
    type: selected?.type ?? exercise.type,
    subType: selected?.sub_type ?? exercise.sub_type,
    equipment: selected?.equipment ?? exercise.equipment,
    muscleGroup: selected?.muscle_group ?? exercise.muscle_group,
    recoverySeconds: exercise.recovery_time_seconds ?? 0,
    sets: sets && sets.length > 0 ? sets : [EMPTY_SET],
    photoUrl:
      fields?.photo_url ||
      exercise.fields?.photo_url ||
      (isRecovery ? undefined : exercisePhotoUrlFromName(title) || undefined),
    videoUrl: fields?.video_url || exercise.fields?.video_url || undefined,
    isSwapped: !!selected,
    isRecovery,
  };
};

export type ResolvedGroup = {
  id: string;
  type: number;
  title: string;
  description: string;
  totalRounds: number;
  totalMinutes: number;
  exercises: ResolvedExercise[];
};

/** Group heading as the mobile `WorkoutV2GroupElement` shows it, `**` substituted. */
export const groupDescription = (group: ExerciseGroupV2Model): string => {
  const template = EXERCISE_GROUP_DESCRIPTIONS[group.type] ?? "";
  if (group.type === EXERCISE_GROUP_TYPE.RFT) {
    return template.replace("**", String(group.total_rounds ?? 0));
  }
  if (
    group.type === EXERCISE_GROUP_TYPE.EMOM ||
    group.type === EXERCISE_GROUP_TYPE.Circuit
  ) {
    return template.replace("**", String(group.total_minutes ?? 0));
  }
  return template;
};

export const resolveGroups = (groups: ExerciseGroupV2Model[]): ResolvedGroup[] =>
  groups.map((group) => ({
    id: group.id,
    type: group.type,
    title: exerciseGroupTypeLabel(group.type),
    description: groupDescription(group),
    totalRounds: group.total_rounds ?? 0,
    totalMinutes: group.total_minutes ?? 0,
    exercises: (group.exercises ?? []).map(exerciseInEffect),
  }));

/**
 * Whether a V2 response describes the workout. Empty, or a first group
 * without exercises, means the workout is a legacy V1 one (mobile rule).
 */
export const isV2Detail = (groups: ExerciseGroupV2Model[] | null | undefined): boolean =>
  !!groups && groups.length > 0 && (groups[0].exercises?.length ?? 0) > 0;

/** Distinct equipment of the exercises in effect, excluding "None" and recovery rows. */
export const equipmentInUse = (groups: ResolvedGroup[]): number[] => {
  const ids = new Set<number>();
  groups.forEach((group) =>
    group.exercises.forEach((exercise) => {
      if (!exercise.isRecovery && exercise.equipment > 0) ids.add(exercise.equipment);
    })
  );
  return Array.from(ids).sort((a, b) => a - b);
};

/** "2min" when a whole number of minutes, otherwise "90sec"; "" for 0. */
export const formatRecovery = (seconds: number | undefined | null): string => {
  if (!seconds || seconds <= 0) return "";
  return seconds % 60 === 0 ? `${seconds / 60}min` : `${seconds}sec`;
};

/** "8" or "8-12" plus the unit, over the sets' values of `property`. */
export const formatRange = (
  sets: SetV2Model[],
  property: keyof SetV2Model,
  unit = ""
): string => {
  if (!sets.length) return `--${unit}`;
  const values = sets.map((set) => set[property] ?? 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  return min === max ? `${min}${unit}` : `${min}-${max}${unit}`;
};

export type RangeField = { title: string; value: string };

const isCardioLike = (type: number): boolean =>
  type === WORKOUT_TYPE.Cardio || type === WORKOUT_TYPE.Interval;

/** The first of watts / speed / rpm / stages that the first set carries (mobile rule). */
export const cardioMetric = (sets: SetV2Model[]): RangeField | null => {
  const first = sets[0];
  if (!first) return null;
  if (first.watts) return { title: "Watts", value: formatRange(sets, "watts") };
  if (first.speed) return { title: "Speed", value: formatRange(sets, "speed") };
  if (first.rpm) return { title: "Rpm", value: formatRange(sets, "rpm") };
  if (first.stages) return { title: "Stages", value: formatRange(sets, "stages") };
  return null;
};

/**
 * The collapsed cardio summary, as the mobile
 * `WorkoutV2SingleExerciseDetailsElement` shows it: Duration, Zone, then the
 * metric the sets carry.
 */
export const cardioDetails = (exercise: ResolvedExercise): RangeField[] => {
  const fields: RangeField[] = [
    { title: "Duration", value: formatRange(exercise.sets, "work_time_minutes", "min") },
    { title: "Zone", value: formatRange(exercise.sets, "work_zone") },
  ];
  const metric = cardioMetric(exercise.sets);
  if (metric) fields.push(metric);
  return fields;
};

export const isCardioExercise = (exercise: ResolvedExercise): boolean =>
  isCardioLike(exercise.type);

/** The collapsed resistance bar: "{n} sets", "{reps} reps" and the first set's weight when set (mobile rule). */
export const setsRepsSummary = (
  exercise: ResolvedExercise
): { sets: number; reps: number; weightKg: number } => ({
  sets: exercise.sets.length,
  reps: exercise.sets[0]?.reps ?? 0,
  weightKg: exercise.sets[0]?.weight_kg ?? 0,
});

export type ExpandedSets = {
  /** "6 Sets" or, for RFT, "4 Rounds". */
  heading: string;
  rows: SetV2Model[];
};

/**
 * What the expanded "Prescribed" list shows, by group type (mobile
 * `WorkoutV2SingleExerciseExpandedElement`): the exercise's sets for None /
 * Superset / EMOM, the first set repeated `total_rounds` times for RFT, and
 * nothing for a Circuit (its rounds exist only once logged).
 */
export const expandedSets = (
  exercise: ResolvedExercise,
  groupType: number,
  totalRounds: number
): ExpandedSets | null => {
  if (groupType === EXERCISE_GROUP_TYPE.Circuit) return null;
  if (groupType === EXERCISE_GROUP_TYPE.RFT) {
    const first = exercise.sets[0] ?? EMPTY_SET;
    const rounds = Math.max(0, totalRounds);
    return {
      heading: `${rounds} Rounds`,
      rows: Array.from({ length: rounds }, () => first),
    };
  }
  return { heading: `${exercise.sets.length} Sets`, rows: exercise.sets };
};

/** Recovery as the expanded rows split it: value and unit ("2", "min"). */
export const splitRecovery = (
  seconds: number | undefined | null
): { value: string; unit: string } | null => {
  if (!seconds || seconds <= 0) return null;
  return seconds % 60 === 0
    ? { value: String(seconds / 60), unit: "min" }
    : { value: String(seconds), unit: "sec" };
};

export const equipmentLabels = (ids: number[]): string[] =>
  ids.map(equipmentLabel).filter((label) => label !== "");

/** Sets logged in the guided workout, keyed as the mobile `ExerciseGroupInstance`. */
export type ExerciseLogs = Record<string, SetV2Model>;

/** The logged sets of a workout: the manual session's `exercise_logs` (mobile rule). */
export const exerciseLogsOf = (record: ExerciseV1Model | null | undefined): ExerciseLogs | null =>
  record?.events?.find((event) => event.input_source === 0)?.exercise_logs ?? null;

/** Rounds the member actually logged for a group (mobile `getExerciseInstanceGroupRounds`). */
export const loggedRounds = (groupIndex: number, logs: ExerciseLogs): number => {
  const prefix = `${groupIndex},`;
  return Object.keys(logs)
    .filter((key) => key.startsWith(prefix))
    .reduce((max, key) => Math.max(max, parseInt(key.split(",")[1], 10) + 1), 0);
};

/**
 * The logged sets for one exercise, in set order: for Superset / None groups
 * the keys are "group,exercise,0,set"; for EMOM / RFT / Circuit they are
 * "group,round,exercise,0", one per round. Null when nothing was logged.
 */
export const loggedSetsFor = (
  logs: ExerciseLogs | null,
  groupIndex: number,
  exerciseIndex: number,
  groupType: number,
  group: ResolvedGroup,
  setCount: number
): SetV2Model[] | null => {
  if (!logs) return null;
  const perSet =
    groupType === EXERCISE_GROUP_TYPE.None || groupType === EXERCISE_GROUP_TYPE.Superset;
  const rounds = perSet
    ? setCount
    : groupType === EXERCISE_GROUP_TYPE.EMOM
    ? group.totalMinutes
    : groupType === EXERCISE_GROUP_TYPE.RFT
    ? group.totalRounds
    : loggedRounds(groupIndex, logs);
  const sets: SetV2Model[] = [];
  for (let index = 0; index < rounds; index++) {
    const key = perSet
      ? `${groupIndex},${exerciseIndex},0,${index}`
      : `${groupIndex},${index},${exerciseIndex},0`;
    const logged = logs[key];
    if (logged) sets.push(logged);
  }
  return sets.length > 0 ? sets : null;
};

/** Mobile: an exercise is skipped when the workout was logged but its group has no logged round. */
export const isExerciseSkipped = (logs: ExerciseLogs | null, groupIndex: number): boolean =>
  !!logs && loggedRounds(groupIndex, logs) === 0;
